// Cashflow API Service
import HttpClient from '../http/HttpClient';
import { LEAD_SERVICE_HOST, LEAD_API_PREFIX } from '../constants/RestConstants';
import {
  CashflowStatement,
  FinancialHealthScore,
  CashflowForecast,
  CashflowSummary,
  CashflowApiResponse,
  CashflowGenerateRequest,
  CashflowForecastRequest,
  CashflowSummaryRequest
} from '../../models/CashflowInterface';

/**
 * Generate Monthly Cash Flow Statement
 * POST /api/v1/cashflow/statements/generate?startDate=2024-01-01&endDate=2024-01-31&periodType=MONTHLY
 */
export async function generateCashflowStatement(request: CashflowGenerateRequest): Promise<CashflowApiResponse<CashflowStatement>> {
  const queryParams = new URLSearchParams({
    startDate: request.startDate,
    endDate: request.endDate,
    periodType: request.periodType
  });

  const response = await HttpClient.POST({
    BASE_URL: LEAD_SERVICE_HOST,
    END_POINT: `${LEAD_API_PREFIX}/cashflow/statements/generate?${queryParams.toString()}`,
    data: {},
  });
  
  return response;
}

/**
 * Get Financial Health Score
 * GET /api/v1/cashflow/health-score
 */
export async function getFinancialHealthScore(): Promise<CashflowApiResponse<FinancialHealthScore>> {
  const response = await HttpClient.GET({
    BASE_URL: LEAD_SERVICE_HOST,
    END_POINT: `${LEAD_API_PREFIX}/cashflow/health-score`,
  });
  
  return response;
}

/**
 * Generate 12-Month Forecast
 * GET /api/v1/cashflow/forecast?months=12
 */
export async function generateCashflowForecast(request: CashflowForecastRequest): Promise<CashflowApiResponse<CashflowForecast>> {
  const queryParams = new URLSearchParams({
    months: request.months.toString()
  });

  const response = await HttpClient.GET({
    BASE_URL: LEAD_SERVICE_HOST,
    END_POINT: `${LEAD_API_PREFIX}/cashflow/forecast?${queryParams.toString()}`,
  });
  
  return response;
}

/**
 * Dashboard Summary
 * GET /api/v1/cashflow/summary?startDate=2024-01-01&endDate=2024-12-31
 */
export async function getCashflowSummary(request: CashflowSummaryRequest): Promise<CashflowApiResponse<CashflowSummary>> {
  const queryParams = new URLSearchParams({
    startDate: request.startDate,
    endDate: request.endDate
  });

  const response = await HttpClient.GET({
    BASE_URL: LEAD_SERVICE_HOST,
    END_POINT: `${LEAD_API_PREFIX}/cashflow/summary?${queryParams.toString()}`,
  });
  
  return response;
}

/**
 * Get cashflow data for a specific date range
 */
export async function getCashflowData(startDate: string, endDate: string) {
  try {
    const [summary, healthScore, forecast] = await Promise.all([
      getCashflowSummary({ startDate, endDate }),
      getFinancialHealthScore(),
      generateCashflowForecast({ months: 12 })
    ]);

    return {
      summary: summary.data,
      healthScore: healthScore.data,
      forecast: forecast.data
    };
  } catch (error) {
    console.error('Error fetching cashflow data:', error);
    throw error;
  }
}

/**
 * Get individual health score data
 */
export async function getHealthScoreData() {
  try {
    const response = await getFinancialHealthScore();
    return response.data;
  } catch (error) {
    console.error('Error fetching health score data:', error);
    throw error;
  }
}

/**
 * Get individual forecast data
 */
export async function getForecastData(months: number = 12) {
  try {
    const response = await generateCashflowForecast({ months });
    return response.data;
  } catch (error) {
    console.error('Error fetching forecast data:', error);
    throw error;
  }
}